import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';
import { Calendar, Download, Check, X } from 'lucide-react';
import { cn } from '@/lib/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface AppointmentSummary {
  id: string;
  slotId: string;
  attendeeEmail: string;
  attendeeName: string;
  status: string;
  bookedAt: string;
  leadId: string | null;
  opportunityId: string | null;
}

interface BookingPage {
  id: string;
  userId: string;
  slug: string;
  title: string;
  description: string | null;
  isActive: boolean;
}

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

const STATUS_COLORS: Record<string, string> = {
  SCHEDULED: 'bg-blue-900/40 text-blue-200',
  CONFIRMED: 'bg-emerald-900/40 text-emerald-200',
  CANCELLED: 'bg-muted text-muted-foreground',
  NO_SHOW: 'bg-red-900/40 text-red-200',
  COMPLETED: 'bg-purple-900/40 text-purple-200',
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export function AppointmentsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<'appointments' | 'booking-pages'>('appointments');
  const [appointments, setAppointments] = useState<AppointmentSummary[]>([]);
  const [bookingPages, setBookingPages] = useState<BookingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<string | null>(null);
  const [copiedSlug, setCopiedSlug] = useState<string | null>(null);

  function flash(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [apptRes, pagesRes] = await Promise.all([
        apiClient.get<AppointmentSummary[]>('/appointments'),
        apiClient.get<BookingPage[]>('/booking-pages'),
      ]);
      setAppointments(apptRes.data);
      setBookingPages(pagesRes.data);
    } catch {
      // keep empty
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  async function updateStatus(id: string, status: string) {
    try {
      await apiClient.patch(`/appointments/${id}/status`, { status });
      flash(t('appointments.status_updated'));
      load();
    } catch {
      flash('Failed to update status');
    }
  }

  function downloadIcs(id: string) {
    const url = `${apiClient.defaults.baseURL}/appointments/${id}/ics`;
    window.open(url, '_blank');
  }

  function copyLink(slug: string) {
    const tenantSlug = 'demo'; // in production: from tenant context
    const url = `${window.location.origin}/book/${tenantSlug}/${slug}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedSlug(slug);
      setTimeout(() => setCopiedSlug(null), 2000);
    });
  }

  const statusLabel = (status: string): string => {
    const key = `appointments.status_${status.toLowerCase()}` as Parameters<typeof t>[0];
    return t(key) ?? status;
  };

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 text-sm shadow-lg">
          {toast}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">{t('appointments.title')}</h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 border-b">
        {(['appointments', 'booking-pages'] as const).map((tb) => (
          <button
            key={tb}
            onClick={() => setTab(tb)}
            className={cn(
              'px-4 py-2 text-sm font-medium transition-colors',
              tab === tb
                ? 'border-b-2 border-primary text-primary'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Calendar className="mr-1.5 inline-block h-4 w-4" />
            {tb === 'appointments' ? t('appointments.title') : t('booking_pages.title')}
          </button>
        ))}
      </div>

      {loading && <p className="text-sm text-muted-foreground">Loading...</p>}

      {/* Appointments tab */}
      {!loading && tab === 'appointments' && (
        <div>
          {appointments.length === 0 ? (
            <div className="rounded-xl border bg-card px-6 py-10 text-center">
              <Calendar className="mx-auto h-10 w-10 text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">{t('appointments.empty')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl border bg-card">
              <table className="w-full text-sm">
                <thead className="border-b bg-card">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {t('appointments.attendee')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      {t('appointments.date_time')}
                    </th>
                    <th className="px-4 py-3 text-left font-medium text-muted-foreground">
                      Status
                    </th>
                    <th className="px-4 py-3 text-right font-medium text-muted-foreground">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {appointments.map((appt) => (
                    <tr key={appt.id}>
                      <td className="px-4 py-3">
                        <div className="font-medium">{appt.attendeeName}</div>
                        <div className="text-xs text-muted-foreground">{appt.attendeeEmail}</div>
                      </td>
                      <td className="px-4 py-3 text-xs text-muted-foreground">
                        {new Date(appt.bookedAt).toLocaleString()}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={cn(
                            'rounded-full px-2 py-0.5 text-[10px] font-medium',
                            STATUS_COLORS[appt.status] ?? 'bg-muted text-foreground',
                          )}
                        >
                          {statusLabel(appt.status)}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex justify-end gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => downloadIcs(appt.id)}
                            title={t('appointments.download_ics')}
                          >
                            <Download className="h-3.5 w-3.5" />
                          </Button>
                          {appt.status === 'SCHEDULED' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => updateStatus(appt.id, 'COMPLETED')}
                              >
                                <Check className="mr-1 h-3 w-3" />
                                {t('appointments.mark_completed')}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => updateStatus(appt.id, 'NO_SHOW')}
                              >
                                <X className="mr-1 h-3 w-3" />
                                {t('appointments.mark_no_show')}
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Booking pages tab */}
      {!loading && tab === 'booking-pages' && (
        <div className="space-y-4">
          {bookingPages.length === 0 ? (
            <div className="rounded-xl border bg-card px-6 py-10 text-center text-sm text-muted-foreground">
              {t('booking_pages.empty')}
            </div>
          ) : (
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {bookingPages.map((page) => (
                <div key={page.id} className="rounded-xl border bg-card p-5">
                  <h3 className="font-medium">{page.title}</h3>
                  <p className="mt-0.5 font-mono text-xs text-muted-foreground">/{page.slug}</p>
                  {page.description && (
                    <p className="mt-2 text-sm text-muted-foreground line-clamp-2">
                      {page.description}
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => copyLink(page.slug)}>
                      {copiedSlug === page.slug ? t('booking_pages.copied') : t('booking_pages.copy_link')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
